/**
 * ProgressReport create/edit form — SCR-016 §5, SCR-017.
 * Used in both the list dialog and the detail edit dialog.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

export const reportFormSchema = z
  .object({
    enrollmentId: z.string().min(1, 'Bắt buộc').optional().or(z.literal('')),
    period: z.enum(['monthly', 'midterm', 'final', 'custom']),
    periodLabel: z.string().max(40).optional().or(z.literal('')),
    periodStart: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().or(z.literal('')),
    periodEnd: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().or(z.literal('')),
    attendanceSummary: z.string().max(500).optional().or(z.literal('')),
    academicComment: z.string().max(1000).optional().or(z.literal('')),
    behaviorComment: z.string().max(1000).optional().or(z.literal('')),
    recommendation: z.string().max(1000).optional().or(z.literal('')),
    overallRating: z.enum(['excellent', 'good', 'average', 'needs_improvement']).optional().or(z.literal('')),
    status: z.enum(['draft', 'published']).default('draft'),
  })
  .refine(
    (d) => {
      if (d.periodStart && d.periodEnd && d.periodEnd < d.periodStart) return false;
      return true;
    },
    { message: 'Cuối kỳ phải sau đầu kỳ', path: ['periodEnd'] },
  );

export type ReportFormValues = z.infer<typeof reportFormSchema>;

interface ReportFormProps {
  isEdit: boolean;
  isSubmitting: boolean;
  defaultValues?: Partial<ReportFormValues>;
  onSubmit: (values: ReportFormValues) => void;
  onCancel: () => void;
}

export function ReportForm({ isEdit, isSubmitting, defaultValues, onSubmit, onCancel }: ReportFormProps) {
  const form = useForm<ReportFormValues>({
    resolver: zodResolver(reportFormSchema),
    defaultValues: {
      period: 'monthly',
      status: 'draft',
      enrollmentId: '',
      periodLabel: '',
      periodStart: '',
      periodEnd: '',
      attendanceSummary: '',
      academicComment: '',
      behaviorComment: '',
      recommendation: '',
      overallRating: '',
      ...defaultValues,
    },
  });

  // Strip empty-string optional fields before sending to server so server Zod
  // receives undefined (not '') for fields like overallRating that use enum.optional().
  function handleFormSubmit(values: ReportFormValues) {
    const cleaned: ReportFormValues = {
      ...values,
      overallRating: values.overallRating === '' ? undefined : values.overallRating,
    };
    onSubmit(cleaned);
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)} className="flex flex-col gap-3">
        {/* enrollmentId — only for create */}
        {!isEdit && (
          <FormField
            control={form.control}
            name="enrollmentId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Mã ghi danh (enrollmentId) <span className="text-destructive">*</span></FormLabel>
                <FormControl>
                  <Input placeholder="ID ghi danh" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormField
            control={form.control}
            name="period"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Kỳ báo cáo <span className="text-destructive">*</span></FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="h-10"><SelectValue placeholder="Chọn kỳ" /></SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="monthly">Hàng tháng</SelectItem>
                    <SelectItem value="midterm">Giữa kỳ</SelectItem>
                    <SelectItem value="final">Cuối kỳ</SelectItem>
                    <SelectItem value="custom">Tùy chỉnh</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="periodLabel"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nhãn kỳ</FormLabel>
                <FormControl><Input placeholder="vd Tháng 5/2026" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="periodStart"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Đầu kỳ</FormLabel>
                <FormControl><Input type="date" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="periodEnd"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Cuối kỳ</FormLabel>
                <FormControl><Input type="date" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="attendanceSummary"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tóm tắt điểm danh</FormLabel>
              <FormControl><Textarea rows={2} maxLength={500} placeholder="Tóm tắt..." {...field} /></FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="academicComment"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nhận xét học lực</FormLabel>
              <FormControl><Textarea rows={2} maxLength={1000} placeholder="Nhận xét..." {...field} /></FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="behaviorComment"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nhận xét hành vi</FormLabel>
              <FormControl><Textarea rows={2} maxLength={1000} placeholder="Nhận xét..." {...field} /></FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="recommendation"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Khuyến nghị</FormLabel>
              <FormControl><Textarea rows={2} maxLength={1000} placeholder="Khuyến nghị..." {...field} /></FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormField
            control={form.control}
            name="overallRating"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Đánh giá tổng quát</FormLabel>
                <Select
                  onValueChange={(v) => field.onChange(v === '__none__' ? '' : v)}
                  value={field.value ? field.value : '__none__'}
                >
                  <FormControl>
                    <SelectTrigger className="h-10"><SelectValue placeholder="Chọn đánh giá" /></SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="__none__">Không đánh giá</SelectItem>
                    <SelectItem value="excellent">Xuất sắc</SelectItem>
                    <SelectItem value="good">Tốt</SelectItem>
                    <SelectItem value="average">Trung bình</SelectItem>
                    <SelectItem value="needs_improvement">Cần cải thiện</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Trạng thái <span className="text-destructive">*</span></FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="h-10"><SelectValue placeholder="Trạng thái" /></SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="draft">Nháp</SelectItem>
                    <SelectItem value="published">Công bố</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
            Hủy
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Đang lưu...' : isEdit ? 'Cập nhật' : 'Tạo báo cáo'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
